import React, { useState, useEffect, useContext } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Box, FormControl, FormLabel, Grid, MenuItem, Select, Stack, Tabs, TextField, Typography } from '@mui/material'
import SelectList from '@/Components/Elements/SelectList'
import QueryBuilderCombinator from './QueryCombinator'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationCircle } from '@fortawesome/pro-duotone-svg-icons/faExclamationCircle'
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck'
import { faCog } from '@fortawesome/free-solid-svg-icons/faCog'
import { faInfo } from '@fortawesome/free-solid-svg-icons/faInfo'
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus'
import { faSave } from '@fortawesome/free-solid-svg-icons/faSave'
import LoadingDefault from '@/Components/Elements/Loading/LoadingDefault'
import { KeDoContext } from '../../../kedo-context'
import { Translation, DefDossierItem, Combinator, Permission, QueryObject, QueryBuilderEditorProps, KeDoContextType } from './types'
import AppDialog from '@/Components/AppDialog'
import { CompactButton, StyledButton } from '@/Components/Elements/StyledComponents/Buttons'
import { StyledVerticalTab } from '@/Components/Elements/StyledComponents/Tabs'
import { TabPanel } from '@/Components/Elements/TabPanel'
import { StyledBreadcrumbs, BreadcrumbLink, BreadcrumbStatic } from '@/Components/Elements/StyledComponents/Breadcrumbs'

const QueryBuilderEditor: React.FC<QueryBuilderEditorProps> = (props) => {
    const { ddId, id, match, history, onSuccess, onClose } = props
    const kedo = useContext<KeDoContextType>(KeDoContext)

    const [errors, setErrors] = useState<Record<string, boolean>>({})
    const [editTrans, setEditTrans] = useState(false)
    const [defdossier, setDefDossier] = useState(ddId)
    const [include_archived, setIncludeArchived] = useState('')
    const [ddObject, setDdObject] = useState<{ id?: number; name_plural?: string }>({})
    const [ddFields, setDdFields] = useState([])
    const [ddLinks, setDdLinks] = useState([])
    const [combinators, setCombinators] = useState<Combinator[]>([])
    const [translations, setTranslations] = useState<Translation[]>([])
    const [loading, setLoading] = useState(false)
    const [permissionUsed, setPermissionUsed] = useState<Permission[]>([])
    const [queryObject, setQueryObject] = useState<QueryObject | null>(null)
    const [showSuccess, setShowSuccess] = useState(false)
    const [saving, setSaving] = useState(false)
    const [showInfo, setShowInfo] = useState(false)
    const [tab, setTab] = useState('translations')

    const onSelectDefDossier = (item: DefDossierItem) => {
        setDefDossier(item.id)
        fetchDefDossier(item.id)
    }

    const onDeleteCombinator = (uuid: string) => {
        setCombinators(combinators.filter((item) => item.uuid !== uuid))
    }

    const addQueryCombinator = () => {
        setCombinators([...combinators, { uuid: kedo.utils().uuidv4() }])
    }

    const onChangeCombinator = (index: number, combinator: Combinator) => {
        const newCombinators = [...combinators]
        newCombinators[index] = combinator
        setCombinators(newCombinators)
    }

    const onChangeTranslation = (locale: string, dashboardName: string | null, searchName: string | null) => {
        const transList: Translation[] = [...translations]
        let trans: Translation | undefined = transList.find((item) => item.culture === locale)
        const filteredList: Translation[] = transList.filter((item) => item.culture !== locale)

        if (!trans) {
            trans = { culture: locale, dashboard_name: dashboardName, search_name: searchName }
        } else if (dashboardName !== null) {
            trans.dashboard_name = dashboardName
        } else if (searchName !== null) {
            trans.search_name = searchName
        }
        setTranslations([...filteredList, trans])
    }

    const fetchItem = () => {
        const url = kedo.api().getDossierQueryEndpoint()
        const itemId = match ? match.params.id : id
        kedo.api()
            .get(url + '/' + itemId)
            .then((response) => {
                let combinatorsData = response.data.data.combinators || []
                combinatorsData = combinatorsData.map((item) => {
                    if (!item.uuid) {
                        item.uuid = kedo.utils().uuidv4()
                    }
                    return item
                })

                setDefDossier(response.data.def_dossier.id)
                setDdObject(response.data.def_dossier)
                setIncludeArchived(response.data?.data?.include_archived ?? '')
                setQueryObject(response.data)
                setCombinators(combinatorsData)
                setTranslations(response.data.translations || [])
            })
    }

    const fetchPermissionQuery = async () => {
        try {
            const url = kedo.api().getDefDossierPermissionEndpoint()
            const itemId = match ? match.params.id : id

            const responseRoles = await kedo.api().get(kedo.api().getRoleEndpoint())
            const roles = responseRoles.data.results
            const permissionsSet = new Set()
            const uniquePermissions = []

            for (const rol of roles) {
                const { data } = await kedo.api().get(url, {
                    params: { queryId: itemId, roleId: rol.id },
                })

                for (const perm of data.results) {
                    if (!permissionsSet.has(perm.id)) {
                        permissionsSet.add(perm.id)
                        uniquePermissions.push(perm)
                    }
                }
            }
            setPermissionUsed(uniquePermissions)
        } catch (error) {
            console.error(error)
        }
    }

    const fetchDefDossier = (id = defdossier) => {
        const ddUrl = kedo.api().getDefDossierEndpoint()
        kedo.api()
            .getCached(ddUrl + '/' + id)
            .then((response) => {
                setDdObject(response.data)
            })
    }

    const handleShowSuccess = () => {
        setShowSuccess(true)
        setTimeout(() => {
            setShowSuccess(false)
        }, 2000)
    }

    const save = () => {
        const envId = kedo.env().getEnvironment().id
        const url = kedo.api().getDossierQueryEndpoint()
        const queryId = match ? parseInt(match.params.id) : id ?? 0
        const item = {
            environment: envId,
            defDossier: defdossier,
            data: {
                combinators: combinators,
                include_archived: include_archived,
            },
            translations: translations,
        }
        setSaving(true)
        if (queryId !== 0) {
            kedo.api()
                .patch(url + '/' + queryId, item)
                .then(() => {
                    kedo.env().reloadEnvironment(envId, kedo.api(), () => {
                        setSaving(false)
                        handleShowSuccess()
                    })
                })
        } else if (queryId === 0) {
            kedo.api()
                .post(url, item)
                .then((response) => {
                    kedo.env().reloadEnvironment(envId, kedo.api(), () => {
                        if (onSuccess) {
                            handleShowSuccess()
                            onSuccess(response.data.id, onClose)
                        } else {
                            window.location = '/querybuilder/' + response.data.id
                        }
                    })
                })
        }
    }

    const renderTranslations = () => {
        const transList = translations || []
        const locales = kedo.env().getEnvironmentLocales()
        if (!locales) {
            return kedo.t('No languages set')
        }

        return locales.map((locale) => {
            let trans: Translation | undefined = transList.find((item) => locale.code === item.culture)
            if (!trans) trans = {}

            return (
                <Box key={locale.code} mb={2}>
                    <FormLabel sx={{ fontSize: '0.875rem', fontWeight: 700, mb: 0 }}>
                        {kedo.t('Search name')} ({locale.code})
                    </FormLabel>
                    <TextField
                        fullWidth
                        size="small"
                        value={trans?.search_name ?? ''}
                        onChange={(event) => onChangeTranslation(locale.code, null, event.target.value)}
                        variant="outlined"
                        margin="dense"
                        sx={{ mb: 2 }}
                    />

                    <FormLabel sx={{ fontSize: '0.875rem', fontWeight: 700, mb: 0 }}>
                        {kedo.t('Dashboard name')} ({locale.code})
                    </FormLabel>
                    <TextField
                        fullWidth
                        size="small"
                        value={trans?.dashboard_name ?? ''}
                        onChange={(event) => onChangeTranslation(locale.code, event.target.value, null)}
                        error={!!errors[locale.code]}
                        variant="outlined"
                        margin="dense"
                    />
                </Box>
            )
        })
    }

    const renderCombinators = () => {
        return combinators.map((combinator, comIndex) => (
            <QueryBuilderCombinator
                level={0}
                index={comIndex}
                key={combinator.uuid}
                onDelete={onDeleteCombinator}
                onChange={onChangeCombinator}
                defdossier={defdossier}
                ddObject={ddObject}
                field={combinator}
                kedo={kedo}
            />
        ))
    }

    const renderInfoModal = () => {
        return (
            <AppDialog
                open
                maxWidth="lg"
                fullWidth
                title={kedo.t('Info')}
                onClose={() => setShowInfo(false)}
                headerClose={() => setShowInfo(false)}
                actions={
                    <StyledButton onClick={() => setShowInfo(false)} color="secondary">
                        {kedo.t('Ok')}
                    </StyledButton>
                }
                contentProps={{ dividers: true, dense: true }}
            >
                <Grid container size={{ xs: 12 }} sx={{ p: 0 }} className={'pillsModalBody'}>
                    <Grid size={{ xs: 12 }}>
                        <table className={'table table-striped'}>
                            <tbody>
                                <tr>
                                    <th>{kedo.t('Id')}</th>
                                    <td>{queryObject?.id}</td>
                                </tr>
                                <tr>
                                    <th>{kedo.t('Uuid')}</th>
                                    <td>{queryObject?.uuid}</td>
                                </tr>
                                <tr>
                                    <th>{kedo.t('Created by')}</th>
                                    <td>{queryObject?.created_by?.username}</td>
                                </tr>
                                <tr>
                                    <th>{kedo.t('Created at')}</th>
                                    <td>{queryObject?.created_at ? kedo.utils().dateFormat(queryObject.created_at) : null}</td>
                                </tr>
                                <tr>
                                    <th>{kedo.t('Modified by')}</th>
                                    <td>{queryObject?.modified_by?.username}</td>
                                </tr>
                                <tr>
                                    <th>{kedo.t('Modified at')}</th>
                                    <td>{queryObject?.modified_at ? kedo.utils().dateFormat(queryObject.modified_at) : null}</td>
                                </tr>
                            </tbody>
                        </table>
                    </Grid>
                </Grid>
            </AppDialog>
        )
    }

    const renderTranslationsModal = () => {
        return (
            <AppDialog
                open
                maxWidth="lg"
                fullWidth
                title={kedo.t('Edit translations')}
                onClose={() => setEditTrans(false)}
                headerClose={() => setEditTrans(false)}
                actions={
                    <StyledButton onClick={() => setEditTrans(false)} color="secondary">
                        {kedo.t('Ok')}
                    </StyledButton>
                }
                contentProps={{ dividers: true, dense: true }}
            >
                <Grid container size={{ xs: 12 }} sx={{ p: 0 }} className={'pillsModalBody'}>
                    <Grid size={{ xs: 12, sm: 3 }} sx={{ borderRight: 1, borderColor: 'divider' }}>
                        <Tabs value={tab} onChange={(_, v) => setTab(v)} orientation="vertical" sx={{ '& .MuiTabs-indicator': { display: 'none' } }}>
                            <StyledVerticalTab label={kedo.t('Translations')} value="translations" />
                        </Tabs>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 9 }} sx={(theme) => ({ p: 2.5 })} className={'pillsContent'}>
                        <TabPanel value={tab} index="translations" disablePadding={true}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {kedo.t('Translations')}
                            </Typography>
                            <FormControl fullWidth margin="normal">
                                {renderTranslations()}
                            </FormControl>
                        </TabPanel>
                    </Grid>
                </Grid>
            </AppDialog>
        )
    }

    const selectDefDossier = () => (
        <Grid size={12}>
            <SelectList
                kedo={kedo}
                title={kedo.t('Select entity')}
                searchName={'search'}
                url={kedo.api().getDefDossierEndpoint() + '?environment=' + kedo.env().getEnvironment().id}
                onDisplayName={(item) => item.id + ' ' + kedo.env().translateItem(item, 'defdossier')}
                onSelect={onSelectDefDossier}
            />
        </Grid>
    )

    const renderButtonGroupBar = () => (
        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
            <Typography variant="h5" component="label" sx={{ minWidth: 80, mr: 1, textAlign: 'right' }}>
                Archive
            </Typography>

            <Select
                value={include_archived}
                displayEmpty
                onChange={(e) => setIncludeArchived(e.target.value)}
                size="small"
                sx={{ minWidth: 150, backgroundColor: 'background.paper' }}
            >
                <MenuItem value="">{kedo.t('Both')}</MenuItem>
                <MenuItem value="none_archived">{kedo.t('None archived')}</MenuItem>
                <MenuItem value="archived">{kedo.t('Archived')}</MenuItem>
            </Select>

            <CompactButton title={kedo.t('Settings')} onClick={() => setEditTrans(true)}>
                <FontAwesomeIcon icon={faCog} />
            </CompactButton>

            {queryObject && (
                <CompactButton onClick={() => setShowInfo(true)}>
                    <FontAwesomeIcon icon={faInfo} />
                </CompactButton>
            )}

            <CompactButton title={'add query combinator'} onClick={addQueryCombinator}>
                <FontAwesomeIcon icon={faPlus} />
            </CompactButton>
        </Stack>
    )

    useEffect(() => {
        if ((match && parseInt(match.params.id) !== 0) || (typeof id === 'number' && id > 0)) {
            fetchItem()
            fetchPermissionQuery()
        } else if (ddId) {
            fetchDefDossier()
        }
    }, [])

    if (loading) {
        return <LoadingDefault />
    }

    return (
        <Box id="kedo-querybuilder" className="mainContainer">
            {editTrans && renderTranslationsModal()}
            {showInfo && renderInfoModal()}
            <Grid container>
                <Grid size={{ xs: 12 }}>
                    <StyledBreadcrumbs>
                        <BreadcrumbLink component={Link} to="/">
                            {kedo.t('Dashboard')}
                        </BreadcrumbLink>
                        <BreadcrumbLink component={Link} to="/querybuilder">
                            {kedo.t('Queries')}
                        </BreadcrumbLink>
                        <BreadcrumbStatic className="current">{kedo.t('Edit')}</BreadcrumbStatic>
                    </StyledBreadcrumbs>
                </Grid>
                <Grid size={{ xs: 12 }} sx={{ mb: 3, mt: 1 }}>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                            {kedo.t('QueryBuilder editor')}
                        </Typography>
                        {defdossier && (
                            <CompactButton onClick={save} startIcon={<FontAwesomeIcon icon={faSave} />}>
                                {saving && <LoadingDefault as="span" />}
                            </CompactButton>
                        )}
                    </Stack>
                </Grid>
                {permissionUsed.length > 0 && (
                    <Grid size={12}>
                        <Alert severity="info">
                            <FontAwesomeIcon icon={faExclamationCircle} /> {permissionUsed.map((p) => p.role.name).toString()}
                        </Alert>
                    </Grid>
                )}
                {showSuccess && (
                    <Grid size={12}>
                        <Alert className={`k-alert alert alert-success alert-shown`} severity={'success'}>
                            <FontAwesomeIcon icon={faCheck} /> {kedo.t('Item has been saved')}
                        </Alert>
                    </Grid>
                )}
                {!defdossier && selectDefDossier()}
                {defdossier && (
                    <Grid size={12} className="card border-primary mb-3">
                        <Grid container alignItems={'center'} className="card-header bg-dark">
                            <Grid>
                                <Typography variant="h5" component="h4" sx={{ fontWeight: 'bold' }}>
                                    {ddObject ? ddObject.name_plural : ''}
                                </Typography>
                            </Grid>
                            <Grid size="auto" sx={{ marginLeft: 'auto' }}>
                                {renderButtonGroupBar()}
                            </Grid>
                        </Grid>
                        <Box className="card-body">{defdossier && renderCombinators()}</Box>
                    </Grid>
                )}
            </Grid>
        </Box>
    )
}

export default QueryBuilderEditor
